import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Plus, MessageSquare, Search, ChevronLeft, ChevronRight, Eye, Upload, X, Image, Loader2, Send, Book, Pencil, XCircle, MoreVertical } from 'lucide-react'
import { format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { supportTicketsApi } from '@/lib/api'
import type { SupportTicket, TicketType, TicketStatus, TicketPriority, CreateTicketRequest, UpdateTicketRequest, TicketAttachment, TicketComment } from '@/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_TYPES = 'ALL_TYPES'
const ALL_STATUSES = 'ALL_STATUSES'
const ALL_PRIORITIES = 'ALL_PRIORITIES'

const ticketTypeOptions: TicketType[] = ['QUERY', 'ISSUE', 'SUGGESTION']
const ticketStatusOptions: TicketStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']
const ticketPriorityOptions: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']

const getStatusBadgeVariant = (status: TicketStatus) => {
  switch (status) {
    case 'OPEN': return 'default'
    case 'IN_PROGRESS': return 'secondary'
    case 'RESOLVED': return 'outline'
    case 'CLOSED': return 'outline'
    default: return 'default'
  }
}

const getPriorityBadgeVariant = (priority: TicketPriority) => {
  switch (priority) {
    case 'URGENT': return 'destructive'
    case 'HIGH': return 'destructive'
    case 'MEDIUM': return 'secondary'
    case 'LOW': return 'outline'
    default: return 'default'
  }
}

const getTypeBadgeVariant = (type: TicketType) => {
  switch (type) {
    case 'QUERY': return 'default'
    case 'ISSUE': return 'destructive'
    case 'SUGGESTION': return 'secondary'
    default: return 'default'
  }
}

export default function SupportTicketsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>(ALL_TYPES)
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_STATUSES)
  const [selectedPriority, setSelectedPriority] = useState<string>(ALL_PRIORITIES)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [viewTicket, setViewTicket] = useState<SupportTicket | null>(null)
  const [openInEditMode, setOpenInEditMode] = useState(false)

  // Form state for creating new ticket
  const [newTicket, setNewTicket] = useState<CreateTicketRequest>({
    ticket_type: 'QUERY',
    subject: '',
    description: '',
    priority: 'MEDIUM',
  })
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch tickets
  const { data: tickets = [], isLoading } = useQuery<SupportTicket[]>({
    queryKey: ['support-tickets'],
    queryFn: () => supportTicketsApi.getMyTickets(),
  })

  // Create ticket mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTicketRequest) => supportTicketsApi.create(data),
    onSuccess: async (ticket) => {
      // Upload attachments if any
      if (selectedFiles.length > 0) {
        setIsUploadingAttachments(true)
        try {
          for (const file of selectedFiles) {
            await supportTicketsApi.uploadAttachment(ticket.ticket_id, file)
          }
          toast.success(t('support.ticketCreatedWithAttachments'))
        } catch {
          toast.warning(t('support.ticketCreatedAttachmentsFailed'))
        } finally {
          setIsUploadingAttachments(false)
        }
      } else {
        toast.success(t('support.ticketCreated'))
      }
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      setIsCreateDialogOpen(false)
      setNewTicket({ ticket_type: 'QUERY', subject: '', description: '', priority: 'MEDIUM' })
      setSelectedFiles([])
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || t('support.createError')
      toast.error(errorMessage)
    },
  })

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        if (!ticket.subject.toLowerCase().includes(searchLower) &&
            !ticket.description.toLowerCase().includes(searchLower)) {
          return false
        }
      }
      if (selectedType !== ALL_TYPES && ticket.ticket_type !== selectedType) return false
      if (selectedStatus !== ALL_STATUSES && ticket.status !== selectedStatus) return false
      if (selectedPriority !== ALL_PRIORITIES && ticket.priority !== selectedPriority) return false
      return true
    })
  }, [tickets, searchTerm, selectedType, selectedStatus, selectedPriority])

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / pageSize)
  const paginatedTickets = filteredTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handleCreateTicket = () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error(t('support.fillRequired'))
      return
    }
    createMutation.mutate(newTicket)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles: File[] = []

    for (const file of files) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error(t('support.invalidFileType', { name: file.name }))
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('support.fileTooLarge', { name: file.name }))
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles(prev => [...prev, ...validFiles])
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedType(ALL_TYPES)
    setSelectedStatus(ALL_STATUSES)
    setSelectedPriority(ALL_PRIORITIES)
    setCurrentPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">{t('support.title')}</h1>
            <p className="text-muted-foreground">{t('support.description')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/support/user-guide">
            <Button variant="outline">
              <Book className="h-4 w-4 mr-2" />
              {t('support.userGuide', 'User Guide')}
            </Button>
          </Link>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('support.createTicket')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  placeholder={t('support.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('support.type')}</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('support.filterType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TYPES}>{t('common.all')}</SelectItem>
                  {ticketTypeOptions.map(type => (
                    <SelectItem key={type} value={type}>{t(`support.types.${type}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('support.status')}</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('support.filterStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>{t('common.all')}</SelectItem>
                  {ticketStatusOptions.map(status => (
                    <SelectItem key={status} value={status}>{t(`support.statuses.${status}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('support.priority')}</Label>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t('support.filterPriority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_PRIORITIES}>{t('common.all')}</SelectItem>
                  {ticketPriorityOptions.map(priority => (
                    <SelectItem key={priority} value={priority}>{t(`support.priorities.${priority}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={clearFilters}>{t('common.clearFilters')}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-400px)] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>{t('support.subject')}</TableHead>
                  <TableHead>{t('support.type')}</TableHead>
                  <TableHead>{t('support.status')}</TableHead>
                  <TableHead>{t('support.priority')}</TableHead>
                  <TableHead>{t('support.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell>
                  </TableRow>
                ) : paginatedTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">{t('support.noTickets')}</TableCell>
                  </TableRow>
                ) : (
                  paginatedTickets.map(ticket => (
                    <TableRow key={ticket.ticket_id}>
                      <TableCell className="font-medium max-w-[300px] truncate">{ticket.subject}</TableCell>
                      <TableCell>
                        <Badge variant={getTypeBadgeVariant(ticket.ticket_type)}>
                          {t(`support.types.${ticket.ticket_type}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(ticket.status)}>
                          {t(`support.statuses.${ticket.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                          {t(`support.priorities.${ticket.priority}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>{format(new Date(ticket.created_at), 'MMM dd, yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setOpenInEditMode(false); setViewTicket(ticket) }}>
                              <Eye className="h-4 w-4 mr-2" />
                              {t('common.view')}
                            </DropdownMenuItem>
                            {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
                              <DropdownMenuItem onClick={() => { setOpenInEditMode(true); setViewTicket(ticket) }}>
                                <Pencil className="h-4 w-4 mr-2" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('common.rowsPerPage')}</span>
          <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1) }}>
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(size => (
                <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('common.pageOf', { current: currentPage, total: totalPages || 1 })}
          </span>
          <Button variant="outline" size="icon" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('support.createTicket')}</DialogTitle>
            <DialogDescription>{t('support.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('support.type')}</Label>
                <Select value={newTicket.ticket_type} onValueChange={(v) => setNewTicket(prev => ({ ...prev, ticket_type: v as TicketType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ticketTypeOptions.map(type => (
                      <SelectItem key={type} value={type}>{t(`support.types.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('support.priority')}</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket(prev => ({ ...prev, priority: v as TicketPriority }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ticketPriorityOptions.map(priority => (
                      <SelectItem key={priority} value={priority}>{t(`support.priorities.${priority}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('support.subject')}</Label>
              <Input value={newTicket.subject} onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))} placeholder={t('support.subjectPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('support.descriptionLabel')}</Label>
              <Textarea value={newTicket.description} onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))} placeholder={t('support.descriptionPlaceholder')} rows={5} />
            </div>
            {/* Screenshot Upload */}
            <div className="space-y-2">
              <Label>{t('support.attachScreenshots')}</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="screenshot-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t('support.selectFiles')}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {t('support.allowedFormats')}
                </span>
              </div>
              {/* Selected files preview */}
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 bg-muted rounded-md px-2 py-1 text-sm"
                    >
                      <Image className="h-4 w-4 text-muted-foreground" />
                      <span className="max-w-[150px] truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateDialogOpen(false); setSelectedFiles([]) }}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateTicket} disabled={createMutation.isPending || isUploadingAttachments}>
              {createMutation.isPending || isUploadingAttachments ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isUploadingAttachments ? t('support.uploadingAttachments') : t('common.creating')}
                </>
              ) : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <ViewTicketDialog
        ticket={viewTicket}
        onClose={() => { setViewTicket(null); setOpenInEditMode(false) }}
        t={t}
        getTypeBadgeVariant={getTypeBadgeVariant}
        getStatusBadgeVariant={getStatusBadgeVariant}
        getPriorityBadgeVariant={getPriorityBadgeVariant}
        ticketTypeOptions={ticketTypeOptions}
        ticketPriorityOptions={ticketPriorityOptions}
        initialEditMode={openInEditMode}
      />
    </div>
  )
}

// Separate component for viewing ticket with attachments and comments
function ViewTicketDialog({
  ticket,
  onClose,
  t,
  getTypeBadgeVariant,
  getStatusBadgeVariant,
  getPriorityBadgeVariant,
  ticketTypeOptions,
  ticketPriorityOptions,
  initialEditMode = false,
}: {
  ticket: SupportTicket | null
  onClose: () => void
  t: (key: string) => string
  getTypeBadgeVariant: (type: TicketType) => 'default' | 'destructive' | 'secondary' | 'outline'
  getStatusBadgeVariant: (status: TicketStatus) => 'default' | 'destructive' | 'secondary' | 'outline'
  getPriorityBadgeVariant: (priority: TicketPriority) => 'default' | 'destructive' | 'secondary' | 'outline'
  ticketTypeOptions: TicketType[]
  ticketPriorityOptions: TicketPriority[]
  initialEditMode?: boolean
}) {
  const queryClient = useQueryClient()
  const [newComment, setNewComment] = useState('')
  const [isEditMode, setIsEditMode] = useState(initialEditMode)
  const [editForm, setEditForm] = useState<UpdateTicketRequest>({})

  // Reset edit mode and form when ticket changes or initialEditMode changes
  useEffect(() => {
    if (ticket) {
      setIsEditMode(initialEditMode)
      setEditForm({
        ticket_type: ticket.ticket_type,
        subject: ticket.subject,
        description: ticket.description,
        priority: ticket.priority,
      })
    }
  }, [ticket, initialEditMode])

  const { data: attachments = [], isLoading: isLoadingAttachments } = useQuery<TicketAttachment[]>({
    queryKey: ['ticket-attachments', ticket?.ticket_id],
    queryFn: () => supportTicketsApi.listAttachments(ticket!.ticket_id),
    enabled: !!ticket,
  })

  const { data: comments = [], isLoading: isLoadingComments, refetch: refetchComments } = useQuery<TicketComment[]>({
    queryKey: ['ticket-comments', ticket?.ticket_id],
    queryFn: () => supportTicketsApi.listComments(ticket!.ticket_id),
    enabled: !!ticket,
  })

  const createCommentMutation = useMutation({
    mutationFn: ({ ticketId, commentText }: { ticketId: string; commentText: string }) =>
      supportTicketsApi.createComment(ticketId, { comment_text: commentText }),
    onSuccess: () => {
      refetchComments()
      setNewComment('')
      toast.success(t('support.commentAdded'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('support.commentError')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: UpdateTicketRequest }) =>
      supportTicketsApi.update(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      toast.success(t('support.ticketUpdated'))
      setIsEditMode(false)
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('support.updateError')
      toast.error(errorMessage)
    },
  })

  const closeTicketMutation = useMutation({
    mutationFn: (ticketId: string) =>
      supportTicketsApi.update(ticketId, { status: 'CLOSED' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      toast.success(t('support.ticketClosed'))
      onClose()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('support.closeError')
      toast.error(errorMessage)
    },
  })

  const handleAddComment = () => {
    if (!ticket || !newComment.trim()) return
    createCommentMutation.mutate({ ticketId: ticket.ticket_id, commentText: newComment.trim() })
  }

  const handleStartEdit = () => {
    if (ticket) {
      setEditForm({
        ticket_type: ticket.ticket_type,
        subject: ticket.subject,
        description: ticket.description,
        priority: ticket.priority,
      })
      setIsEditMode(true)
    }
  }

  const handleSaveEdit = () => {
    if (!ticket) return
    updateMutation.mutate({ ticketId: ticket.ticket_id, data: editForm })
  }

  const handleCloseTicket = () => {
    if (!ticket) return
    closeTicketMutation.mutate(ticket.ticket_id)
  }

  const isTicketClosed = ticket?.status === 'CLOSED' || ticket?.status === 'RESOLVED'

  return (
    <Dialog open={!!ticket} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('support.editTicket') : ticket?.subject}</DialogTitle>
        </DialogHeader>
        {ticket && (
          <div className="space-y-4">
            {isEditMode ? (
              // Edit Mode
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('support.type')}</Label>
                    <Select value={editForm.ticket_type} onValueChange={(v) => setEditForm(prev => ({ ...prev, ticket_type: v as TicketType }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ticketTypeOptions.map(type => (
                          <SelectItem key={type} value={type}>{t(`support.types.${type}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('support.priority')}</Label>
                    <Select value={editForm.priority} onValueChange={(v) => setEditForm(prev => ({ ...prev, priority: v as TicketPriority }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ticketPriorityOptions.map(priority => (
                          <SelectItem key={priority} value={priority}>{t(`support.priorities.${priority}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('support.subject')}</Label>
                  <Input value={editForm.subject || ''} onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>{t('support.descriptionLabel')}</Label>
                  <Textarea value={editForm.description || ''} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} rows={5} />
                </div>
              </>
            ) : (
              // View Mode
              <>
                <div className="flex gap-2">
                  <Badge variant={getTypeBadgeVariant(ticket.ticket_type)}>{t(`support.types.${ticket.ticket_type}`)}</Badge>
                  <Badge variant={getStatusBadgeVariant(ticket.status)}>{t(`support.statuses.${ticket.status}`)}</Badge>
                  <Badge variant={getPriorityBadgeVariant(ticket.priority)}>{t(`support.priorities.${ticket.priority}`)}</Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('support.descriptionLabel')}</Label>
                  <p className="mt-1 whitespace-pre-wrap">{ticket.description}</p>
                </div>
                {/* Attachments */}
                {isLoadingAttachments ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('support.loadingAttachments')}
                  </div>
                ) : attachments.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">{t('support.attachments')}</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {attachments.map((attachment) => (
                        <a
                          key={attachment.attachment_id}
                          href={attachment.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-md border hover:bg-muted transition-colors"
                        >
                          <Image className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">{attachment.file_name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {/* Comments Section */}
                <div>
                  <Label className="text-muted-foreground">{t('support.comments')}</Label>
                  <div className="mt-2 h-[200px] overflow-y-auto border rounded-md p-3">
                    {isLoadingComments ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : comments.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center">{t('support.noComments')}</p>
                    ) : (
                      <div className="space-y-3">
                        {comments.map((comment) => (
                          <div key={comment.comment_id} className="border-b pb-2 last:border-b-0">
                            <div className="flex justify-between items-start">
                              <span className="text-sm font-medium">{comment.user_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                              </span>
                            </div>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{comment.comment_text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Add Comment */}
                  <div className="mt-2 flex gap-2">
                    <Input
                      placeholder={t('support.addCommentPlaceholder')}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleAddComment()
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || createCommentMutation.isPending}
                    >
                      {createCommentMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('support.createdAt')}: {format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm')}
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {isEditMode ? (
            <>
              <Button variant="outline" onClick={() => setIsEditMode(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
              {!isTicketClosed && (
                <>
                  <Button variant="outline" onClick={handleStartEdit}>
                    <Pencil className="h-4 w-4 mr-2" />
                    {t('common.edit')}
                  </Button>
                  <Button variant="destructive" onClick={handleCloseTicket} disabled={closeTicketMutation.isPending}>
                    {closeTicketMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                    {t('support.closeTicket')}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
